import { CacheError, requestToPromise, runTransaction } from "./cache-database";
import type { StoreName } from "./cache-schema";

export interface CacheStore< T > {
    readonly name: StoreName;
    /** Every value, in ascending key order. */
    all(): Promise< T[] >;
    /** Every [ key, value ] pair, in ascending key order. */
    entries(): Promise< Array< [ string, T ] > >;
    keys(): Promise< string[] >;
    /** `undefined` when the key is absent. `V` lets the untyped state store read a specific type. */
    get< V = T >( key: string ): Promise< V | undefined >;
    count(): Promise< number >;
    isEmpty(): Promise< boolean >;
    put( key: string, value: T ): Promise< void >;
    /** One readwrite transaction: every entry lands or none does. */
    putMany( entries: ReadonlyArray< readonly [ string, T ] > ): Promise< void >;
    delete( key: string ): Promise< void >;
    /** One readwrite transaction. Absent keys are ignored. */
    deleteMany( keys: ReadonlyArray< string > ): Promise< void >;
    clear(): Promise< void >;
}

export function createStore< T >( name: StoreName ): CacheStore< T > {
    async function wrap< R >( operation: string, work: () => Promise< R > ): Promise< R > {
        try {
            return await work();
        } catch ( err: unknown ) {
            if ( err instanceof CacheError ) {
                throw err;
            }
            const detail = ( err as { name?: string; message?: string } | null );
            throw new CacheError( `[ cache ] ${ name }.${ operation } failed: ${ detail?.name ?? 'Error' }${ detail?.message ? ` — ${ detail.message }` : '' }`, { cause: err } );
        }
    }

    const read = < R >( operation: string, request: ( store: IDBObjectStore ) => IDBRequest< R > ): Promise< R > =>
        wrap( operation, () => runTransaction( [ name ], 'readonly', tx => requestToPromise( request( tx.objectStore( name ) ) ) ) );

    const write = ( operation: string, apply: ( store: IDBObjectStore ) => void ): Promise< void > =>
        wrap( operation, () => runTransaction( [ name ], 'readwrite', tx => { apply( tx.objectStore( name ) ); } ) );

    return {
        name,
        all: () => read( 'all', store => store.getAll() as IDBRequest< T[] > ),
        // Keys are strings by construction: every write path types `key: string`. IDBValidKey[] is narrowed here once.
        keys: () => read( 'keys', store => store.getAllKeys() as IDBRequest< string[] > ),
        entries: () => wrap( 'entries', () => runTransaction( [ name ], 'readonly', async tx => {
            const store = tx.objectStore( name );
            // Both requests run in the same readonly transaction and both return ascending key order, so zipping by index is exact.
            const [ keys, values ] = await Promise.all( [
                requestToPromise( store.getAllKeys() as IDBRequest< string[] > ),
                requestToPromise( store.getAll() as IDBRequest< T[] > )
            ] );
            return keys.map( ( key, i ) => [ key, values[ i ] ] as [ string, T ] );
        } ) ),
        get: < V = T >( key: string ) => read( 'get', store => store.get( key ) as IDBRequest< V | undefined > ),
        count: () => read( 'count', store => store.count() ),
        isEmpty: async () => 0 === await read( 'isEmpty', store => store.count() ),
        put: ( key, value ) => write( 'put', store => { store.put( value, key ); } ),
        putMany: entries => 0 === entries.length
            ? Promise.resolve()
            : write( 'putMany', store => { for ( const [ key, value ] of entries ) store.put( value, key ); } ),
        delete: key => write( 'delete', store => { store.delete( key ); } ),
        deleteMany: keys => 0 === keys.length
            ? Promise.resolve()
            : write( 'deleteMany', store => { for ( const key of keys ) store.delete( key ); } ),
        clear: () => write( 'clear', store => { store.clear(); } )
    };
}
